<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useFileDialog } from '@vueuse/core'
import { useSortable } from '@vueuse/integrations/useSortable'
import DropZone from '@/modules/filing/components/DropZone.vue'
import { VButton, VIcon } from '@/modules/shared/components'

///////////////////////////////////////////////////////////////////////////////
// Config
///////////////////////////////////////////////////////////////////////////////

const MAX_FILE_SIZE_HUMAN = '50MB'
const MAX_PAGES_HUMAN = '2,000'

const props = withDefaults(
  defineProps<{
    accept: string[]
    disabled?: boolean
    existing_files?: string[]
    custom_label: string
  }>(),
  {
    accept: [],
    disabled: false,
    existing_files: null,
    custom_label: '',
  },
)

const emit = defineEmits(['chosen'])

///////////////////////////////////////////////////////////////////////////////
// File management
///////////////////////////////////////////////////////////////////////////////

const files = ref<File[]>([])
const isProcessing = ref(false)

const addFile = (file: File) => {
  const is_acceptable = props.accept.some((a) => file.name.endsWith(a))
  const is_duplicate = files.value.some((f) => f.name === file.name && f.size === file.size)

  if (is_acceptable && !is_duplicate) {
    files.value.push(file)
  }
}

const removeFile = (index: number) => {
  files.value.splice(index, 1)
}

const processFiles = (incomingFiles: File[]) => {
  if (props.disabled) return
  isProcessing.value = true
  incomingFiles.forEach(addFile)
  isProcessing.value = false
}

watch(
  files,
  (newVal) => {
    emit('chosen', newVal)
  },
  { deep: true },
)

///////////////////////////////////////////////////////////////////////////////
// File dialog
///////////////////////////////////////////////////////////////////////////////

const { files: fd_files, open } = useFileDialog({ accept: props.accept.join(',') })

watch(fd_files, (fileList) => {
  if (!fileList) return
  const fileArray = Array.from(fileList) as File[]
  processFiles(fileArray)
})

const handleDropZoneDrop = (event: DragEvent) => {
  if (props.disabled) return
  const droppedFiles = event.dataTransfer?.files
  if (!droppedFiles?.length) return

  processFiles(Array.from(droppedFiles) as File[])
}

const dropZoneState = computed(() => (props.disabled || isProcessing.value ? 'uploading' : 'default'))

///////////////////////////////////////////////////////////////////////////////
// Sorting
///////////////////////////////////////////////////////////////////////////////

const sortable_ref = ref<HTMLElement | null>(null)

useSortable(sortable_ref, files, {
  handle: '.handle',
})

///////////////////////////////////////////////////////////////////////////////
// Fomatting
///////////////////////////////////////////////////////////////////////////////

const format_extension = (extension: string) => extension.substring(1).toUpperCase()

const formatted_accept = props.accept.map(format_extension).join(', ')
</script>
<template>
  <div class="space-y-2">
    <div class="">
      <DropZone
        class="mb-2 !rounded-md border border-gray-300 shadow-sm"
        :state="dropZoneState"
        @click="open"
        @drop.prevent="handleDropZoneDrop"
        @dragover.prevent
      />
    </div>
    <div class="text-center text-sm text-gray-600">
      <span v-if="custom_label" class="font-medium">{{ custom_label }}</span>
      <span class="block text-xs text-gray-500">
        {{ formatted_accept }} (&#60;{{ MAX_FILE_SIZE_HUMAN }} and &#60;{{ MAX_PAGES_HUMAN }} pages)
      </span>
    </div>
  </div>

  <div v-show="files.length || existing_files" class="mt-6">
    <div class="rounded-t-md border border-b-0 bg-gray-50 px-3 py-1 shadow-sm">
      <span class="text-xs font-semibold uppercase tracking-wide">Chosen files</span>
    </div>
    <div>
      <!-- error drawer -->
    </div>

    <!-- file list -->
    <ul ref="sortable_ref" class="rounded-b-md border border-gray-200 text-sm text-gray-600">
      <li
        v-for="(file, index) in files"
        :key="file.name"
        class="flex items-center space-x-2 border-b border-gray-100 px-1.5 py-1.5 last:border-none"
      >
        <!-- prettier-ignore -->
        <VButton
          class="handle"
          cursor="move"
          :disabled="disabled"
          size="xs-narrow" 
          variant="invisible"
        >
          <span class="text-gray-400">
            <VIcon name="Move" />
          </span>
        </VButton>
        <div class="flex-grow text-sm">{{ file.name }}</div>
        <!-- prettier-ignore -->
        <VButton
          @click="removeFile(index)"
          :disabled="disabled"
          size="xs-narrow"
          variant="invisible"
        >
          <VIcon name="X" />
        </VButton>
      </li>
    </ul>

    <ul class="rounded-b-md border border-gray-200 text-sm text-gray-600" v-if="existing_files && !files.length">
      <li
        v-for="(file, index) in existing_files"
        :key="file"
        class="flex items-center space-x-2 border-b border-gray-100 py-1.5 pl-3 pr-1.5 last:border-none"
      >
        <div class="flex-grow text-sm">{{ file }}</div>
      </li>
    </ul>
  </div>
</template>
